import { Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { SelectionGrid, type SelectionGridColumn } from "src/components/ReferenceSearch";
import {
    getPackagingKind,
    PackagingGridEntity,
    PackagingKind,
    PackagingPickerSource,
    usePackagingListGridCore,
} from "src/pages/Product/ProductForm/Pages/Page3/PackagingsModal/packagingListGridCore";
import { ProductFormProductPackaging } from "src/pages/Product/ProductForm/ProductForm.types";
import {
    referenceSearchPrimaryNameSx,
    referenceSearchSecondaryTextSx,
} from "src/components/ReferenceSearch/referenceSearchTextStyles";
import { PackagingTypeTag } from "src/components/Packaging/PackagingTypeTag";

type PackagingListGridV2Props = {
    delayedSearch: string;
    onSelectPackaging: (packaging: ProductFormProductPackaging) => void;
    source: PackagingPickerSource;
};

const PACKAGING_SEARCH_PAGE_SIZE = 10;
const NO_VALUE = "-";

export const PackagingListGridV2 = (props: PackagingListGridV2Props) => {
    const { delayedSearch, onSelectPackaging, source } = props;
    const { t } = useTranslation();
    const {
        isLoading,
        newPackagingHandler,
        packagings,
        pagination,
        selectedCompany,
        setPagination,
        source: resolvedSource,
        totalItems,
    } = usePackagingListGridCore(delayedSearch, PACKAGING_SEARCH_PAGE_SIZE, source);

    if (selectedCompany === null) return null;

    const selectPackaging = (packaging: PackagingGridEntity) => {
        onSelectPackaging(newPackagingHandler(packaging));
    };
    const getPackagingKindLabel = (kind: PackagingKind) =>
        kind === "element" ? t("packaging.element", "Element") : t("packaging.packaging", "Packaging");
    const renderPackagingType = (row: PackagingGridEntity) => {
        const kind = getPackagingKind(row);

        return kind === null ?
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {NO_VALUE}
                </Typography>
            :   <PackagingTypeTag kind={kind} label={getPackagingKindLabel(kind)} />;
    };
    const repositoryColumns: SelectionGridColumn<PackagingGridEntity>[] = [
        {
            id: "name",
            width: "55%",
            render: (row) => (
                <Typography variant="body-bold" sx={referenceSearchPrimaryNameSx}>
                    {row.name}
                </Typography>
            ),
        },
        {
            id: "identifier",
            width: "27%",
            render: (row) => (
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {("kind" in row ? row.code : row.externalId) ?? NO_VALUE}
                </Typography>
            ),
        },
        {
            id: "type",
            width: "18%",
            render: renderPackagingType,
        },
    ];
    const materialColumns: SelectionGridColumn<PackagingGridEntity>[] = [
        {
            id: "name",
            width: "82%",
            render: (row) => (
                <Typography variant="body-bold" sx={referenceSearchPrimaryNameSx}>
                    {row.name}
                </Typography>
            ),
        },
        {
            id: "type",
            width: "18%",
            render: renderPackagingType,
        },
    ];
    const pageCount = totalItems === 0 ? 0 : Math.ceil(totalItems / pagination.pageSize);

    const handlePageChange = (value: number) => {
        setPagination((currentPagination) => ({
            ...currentPagination,
            pageIndex: value - 1,
        }));
    };

    return (
        <SelectionGrid
            columns={resolvedSource === "repository" ? repositoryColumns : materialColumns}
            emptyContent={
                <Typography variant="body-regular" sx={referenceSearchSecondaryTextSx}>
                    {t("common.noResults", "No results")}
                </Typography>
            }
            getRowId={(row) => row["@id"] ?? ("compositeId" in row ? row.compositeId : String(row.id))}
            isLoading={isLoading}
            onPageChange={handlePageChange}
            onSelect={selectPackaging}
            page={pagination.pageIndex + 1}
            pageCount={pageCount}
            rows={packagings}
            maxVisibleRows={6}
        />
    );
};
